import type { Logger } from './logger';
import type { LogContext } from './types';
import { buildFingerprint, getErrorEventId, newEventId, shouldReportFingerprint } from './error-identity';

export interface ReportClientErrorOptions {
  dedupeWindowMs?: number;
  force?: boolean;
  reporter?: string;
}

export function reportClientError(
  logger: Logger,
  error: Error | string,
  context?: Partial<LogContext>,
  options: ReportClientErrorOptions = {}
): string | null {
  const errorEventId =
    typeof context?.error_event_id === 'string'
      ? context.error_event_id
      : typeof error === 'string'
        ? newEventId('err')
        : getErrorEventId(error);

  const errorFingerprint =
    typeof context?.error_fingerprint === 'string'
      ? context.error_fingerprint
      : buildFingerprint([
          context?.component,
          context?.action,
          typeof error === 'string' ? 'Error' : error.name,
          typeof error === 'string' ? error : error.message,
          context?.source_file,
          context?.error_line,
          context?.error_col,
        ]);

  if (
    !options.force &&
    !shouldReportFingerprint(errorFingerprint, options.dedupeWindowMs)
  ) {
    return null;
  }

  const nextContext: Partial<LogContext> = {
    ...context,
    error_event_id: errorEventId,
    error_fingerprint: errorFingerprint,
    ...(options.reporter ? { error_reporter: options.reporter } : {}),
  };

  if (typeof error === 'string') {
    logger.error(error, nextContext);
  } else {
    logger.logError(error, nextContext);
  }

  logger.reportExternalError({
    error,
    context: nextContext,
    eventId: errorEventId,
    fingerprint: errorFingerprint,
  });

  return errorEventId;
}
